import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Divider,
  Button,
  LinearProgress,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InsightsIcon from "@mui/icons-material/Insights";
import SecurityIcon from "@mui/icons-material/Security";
import PieChartIcon from "@mui/icons-material/PieChart";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  Area,
  ComposedChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const fmtINR = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const fmtPct = (n) => `${Number(n || 0).toFixed(2)}%`;

function MetallicTile({
  title,
  subtitle,
  icon,
  right,
  children,
  sx,
  headerSx,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Metallic + glass feel
  const bg = isDark
    ? "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))"
    : "linear-gradient(135deg, rgba(255,255,255,0.9), rgba(230,232,238,0.65))";

  const shine = isDark
    ? "linear-gradient(120deg, rgba(255,255,255,0.14), rgba(255,255,255,0) 55%)"
    : "linear-gradient(120deg, rgba(255,255,255,0.75), rgba(255,255,255,0) 55%)";

  const border = isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.08)";

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 3,
        border,
        background: bg,
        backdropFilter: "blur(10px)",
        p: 2,
        minHeight: 140,
        boxShadow: isDark
          ? "0 12px 34px rgba(0,0,0,0.35)"
          : "0 18px 38px rgba(15,23,42,0.10)",
        "&:before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: shine,
          pointerEvents: "none",
        },
        "&:after": {
          content: '""',
          position: "absolute",
          top: -60,
          right: -60,
          width: 180,
          height: 180,
          background: isDark
            ? "radial-gradient(circle, rgba(148,163,184,0.22), rgba(148,163,184,0) 65%)"
            : "radial-gradient(circle, rgba(99,102,241,0.18), rgba(99,102,241,0) 65%)",
          transform: "rotate(12deg)",
          pointerEvents: "none",
        },
        ...sx,
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ position: "relative", zIndex: 1, ...headerSx }}
      >
        <Stack spacing={0.3}>
          <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <Typography variant="subtitle2" sx={{ opacity: 0.85, letterSpacing: 0.4 }}>
              {title}
            </Typography>
          </Stack>
          {subtitle ? (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Stack>

        {right ? <Box>{right}</Box> : null}
      </Stack>

      <Box sx={{ mt: 1.6, position: "relative", zIndex: 1 }}>{children}</Box>
    </Paper>
  );
}

export default function EtfDashboard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // ---------- MOCK DATA (replace with API later) ----------
  const summary = useMemo(
    () => ({
      fundName: "PASSIVEST TRACKER",
      nav: 127.65,
      navChangePct: 0.86,
      aum: 4280000000,
      expenseRatio: 0.32,
      trackingError: 0.18,
      risk: "Moderate",
      rating: 4.3,
    }),
    []
  );

  const perf = useMemo(
    () => [
      { name: "1W", v: 1.2 },
      { name: "1M", v: 3.8 },
      { name: "3M", v: 8.7 },
      { name: "6M", v: 12.4 },
      { name: "1Y", v: 22.9 },
    ],
    []
  );

  const navSeries = useMemo(
    () => [
      { d: "Sep", nav: 98, vol: 42 },
      { d: "Oct", nav: 104, vol: 48 },
      { d: "Nov", nav: 112, vol: 55 },
      { d: "Dec", nav: 118, vol: 61 },
      { d: "Jan", nav: 127.65, vol: 58 },
    ],
    []
  );

  const allocation = useMemo(
    () => [
      { name: "IT", value: 28 },
      { name: "Banking", value: 22 },
      { name: "Energy", value: 14 },
      { name: "FMCG", value: 12 },
      { name: "Auto", value: 10 },
      { name: "Others", value: 14 },
    ],
    []
  );

  const holdings = useMemo(
    () => [
      { sym: "RELIANCE", w: 9.8, chg: 1.2 },
      { sym: "HDFCBANK", w: 8.1, chg: 0.6 },
      { sym: "INFY", w: 7.6, chg: 1.9 },
      { sym: "ICICIBANK", w: 6.9, chg: 0.3 },
      { sym: "TCS", w: 6.5, chg: 1.1 },
    ],
    []
  );

  const alerts = useMemo(
    () => [
      { t: "Rebalance scheduled", d: "Next rebalance: 2026-01-15", sev: "info" },
      { t: "Tracking tight", d: "Tracking error is within target band", sev: "success" },
      { t: "Expense review", d: "Expense ratio higher than peer median", sev: "warning" },
    ],
    []
  );

  const pieColors = isDark
    ? ["#94a3b8", "#a78bfa", "#60a5fa", "#34d399", "#fbbf24", "#fb7185"]
    : ["#334155", "#6d28d9", "#2563eb", "#059669", "#d97706", "#e11d48"];

  // ---------- PAGE BACKGROUND ----------
  const pageBg = isDark
    ? "radial-gradient(1200px 600px at 15% 10%, rgba(167,139,250,0.18), rgba(0,0,0,0)), radial-gradient(900px 500px at 85% 20%, rgba(96,165,250,0.15), rgba(0,0,0,0)), radial-gradient(900px 600px at 50% 95%, rgba(148,163,184,0.12), rgba(0,0,0,0))"
    : "radial-gradient(1100px 600px at 15% 10%, rgba(109,40,217,0.12), rgba(255,255,255,0)), radial-gradient(900px 500px at 85% 20%, rgba(37,99,235,0.10), rgba(255,255,255,0)), radial-gradient(900px 600px at 50% 95%, rgba(15,23,42,0.06), rgba(255,255,255,0))";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 1.5, md: 2.5 },
        background: pageBg,
      }}
    >
      {/* Top Bar */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
            {summary.fundName}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            NAV, allocation, performance, risk signals
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={`Risk: ${summary.risk}`}
            variant="outlined"
            sx={{
              borderRadius: 999,
              borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(15,23,42,0.12)",
            }}
          />
          <Chip
            label={`Rating: ${summary.rating}`}
            variant="outlined"
            sx={{
              borderRadius: 999,
              borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(15,23,42,0.12)",
            }}
          />
          <Tooltip title="Refresh (demo)">
            <IconButton onClick={() => {}} sx={{ borderRadius: 2 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Bento Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
          gap: 2,
        }}
      >
        {/* NAV Tile - big */}
        <MetallicTile
          title="NAV"
          subtitle="Latest value + daily move"
          icon={<TrendingUpIcon fontSize="small" sx={{ opacity: 0.85 }} />}
          right={
            <Button
              size="small"
              variant="contained"
              startIcon={<InsightsIcon />}
              sx={{
                borderRadius: 2,
                background:
                  isDark
                    ? "linear-gradient(135deg, rgba(167,139,250,0.85), rgba(96,165,250,0.85))"
                    : "linear-gradient(135deg, rgba(109,40,217,0.95), rgba(37,99,235,0.95))",
              }}
            >
              Insights
            </Button>
          }
          sx={{ gridColumn: { xs: "1 / -1", md: "span 5" }, minHeight: 220 }}
        >
          <Stack direction="row" alignItems="baseline" spacing={1.2}>
            <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: -0.6 }}>
              {summary.nav.toFixed(2)}
            </Typography>
            <Chip
              label={`${summary.navChangePct >= 0 ? "+" : ""}${fmtPct(summary.navChangePct)}`}
              size="small"
              sx={{
                borderRadius: 999,
                fontWeight: 700,
                color: isDark ? "#0b1020" : "#fff",
                background:
                  summary.navChangePct >= 0
                    ? isDark
                      ? "linear-gradient(135deg, rgba(52,211,153,0.95), rgba(167,243,208,0.9))"
                      : "linear-gradient(135deg, rgba(5,150,105,0.95), rgba(34,197,94,0.9))"
                    : "linear-gradient(135deg, rgba(239,68,68,0.95), rgba(251,113,133,0.9))",
              }}
            />
          </Stack>

          <Box sx={{ mt: 1.5, height: 140 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={navSeries}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="d" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} domain={["dataMin - 2", "dataMax + 2"]} />
                <RTooltip />
                <Line
                  type="monotone"
                  dataKey="nav"
                  strokeWidth={3}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </MetallicTile>

        {/* AUM Tile */}
        <MetallicTile
          title="AUM"
          subtitle="Assets under management"
          icon={<SecurityIcon fontSize="small" sx={{ opacity: 0.85 }} />}
          sx={{ gridColumn: { xs: "1 / -1", md: "span 3" }, minHeight: 220 }}
        >
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            {fmtINR(summary.aum)}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.5 }}>
            Liquidity looks healthy. Spread stable in last month.
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Liquidity score
            </Typography>
            <LinearProgress variant="determinate" value={82} sx={{ mt: 0.8, borderRadius: 999 }} />
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.8 }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                0
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                100
              </Typography>
            </Stack>
          </Box>
        </MetallicTile>

        {/* Expense + Tracking */}
        <MetallicTile
          title="Costs & Tracking"
          subtitle="Expense ratio + tracking error"
          icon={<ReceiptLongIcon fontSize="small" sx={{ opacity: 0.85 }} />}
          sx={{ gridColumn: { xs: "1 / -1", md: "span 4" }, minHeight: 220 }}
        >
          <Stack spacing={1.2}>
            <Box>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  Expense Ratio
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {fmtPct(summary.expenseRatio)}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, (summary.expenseRatio / 1.0) * 100)}
                sx={{ mt: 0.8, borderRadius: 999 }}
              />
            </Box>

            <Box>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  Tracking Error
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {fmtPct(summary.trackingError)}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, (summary.trackingError / 1.0) * 100)}
                sx={{ mt: 0.8, borderRadius: 999 }}
              />
            </Box>

            <Divider sx={{ opacity: 0.25 }} />

            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              Tip: Keep tracking error low by optimizing rebalance window + reducing turnover.
            </Typography>
          </Stack>
        </MetallicTile>

        {/* Performance Tile */}
        <MetallicTile
          title="Performance"
          subtitle="Return buckets"
          icon={<TrendingUpIcon fontSize="small" sx={{ opacity: 0.85 }} />}
          sx={{ gridColumn: { xs: "1 / -1", md: "span 4" }, minHeight: 240 }}
        >
          <Box sx={{ height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={perf}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <RTooltip />
                <Bar dataKey="v" barSize={26} radius={[8, 8, 0, 0]} />
                <Area type="monotone" dataKey="v" fillOpacity={0.08} />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>

          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip label="Vol: Medium" size="small" sx={{ borderRadius: 999 }} />
            <Chip label="Trend: Up" size="small" sx={{ borderRadius: 999 }} />
          </Stack>
        </MetallicTile>

        {/* Allocation Tile */}
        <MetallicTile
          title="Sector Allocation"
          subtitle="Weight distribution"
          icon={<PieChartIcon fontSize="small" sx={{ opacity: 0.85 }} />}
          sx={{ gridColumn: { xs: "1 / -1", md: "span 4" }, minHeight: 240 }}
        >
          <Box sx={{ height: 170 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <RTooltip />
                <Pie
                  data={allocation}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={52}
                  outerRadius={78}
                  paddingAngle={2}
                >
                  {allocation.map((_, i) => (
                    <Cell key={i} fill={pieColors[i % pieColors.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Box>

          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
            {allocation.slice(0, 4).map((x) => (
              <Chip
                key={x.name}
                size="small"
                label={`${x.name} ${x.value}%`}
                sx={{ borderRadius: 999 }}
              />
            ))}
          </Stack>
        </MetallicTile>

        {/* Holdings Tile - wide */}
        <MetallicTile
          title="Top Holdings"
          subtitle="Top 5 by weight"
          icon={<InsightsIcon fontSize="small" sx={{ opacity: 0.85 }} />}
          sx={{ gridColumn: { xs: "1 / -1", md: "span 7" }, minHeight: 240 }}
        >
          <Stack spacing={1.2}>
            {holdings.map((h) => (
              <Box key={h.sym}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {h.sym}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={`${h.w.toFixed(1)}%`}
                      sx={{ borderRadius: 999 }}
                    />
                    <Chip
                      size="small"
                      label={`${h.chg >= 0 ? "+" : ""}${fmtPct(h.chg)}`}
                      sx={{
                        borderRadius: 999,
                        color: isDark ? "#0b1020" : "#fff",
                        background:
                          h.chg >= 0
                            ? isDark
                              ? "linear-gradient(135deg, rgba(52,211,153,0.95), rgba(167,243,208,0.9))"
                              : "linear-gradient(135deg, rgba(5,150,105,0.95), rgba(34,197,94,0.9))"
                            : "linear-gradient(135deg, rgba(239,68,68,0.95), rgba(251,113,133,0.9))",
                      }}
                    />
                  </Stack>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, h.w * 8)}
                  sx={{ mt: 0.8, borderRadius: 999 }}
                />
              </Box>
            ))}
          </Stack>
        </MetallicTile>

        {/* Alerts Tile */}
        <MetallicTile
          title="Signals"
          subtitle="Ops + risk notes"
          icon={<SecurityIcon fontSize="small" sx={{ opacity: 0.85 }} />}
          sx={{ gridColumn: { xs: "1 / -1", md: "span 5" }, minHeight: 240 }}
        >
          <Stack spacing={1}>
            {alerts.map((a, idx) => (
              <Paper
                key={idx}
                elevation={0}
                sx={{
                  p: 1.2,
                  borderRadius: 2,
                  background: isDark
                    ? "rgba(255,255,255,0.04)"
                    : "rgba(15,23,42,0.03)",
                  border: isDark
                    ? "1px solid rgba(255,255,255,0.08)"
                    : "1px solid rgba(15,23,42,0.06)",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {a.t}
                  </Typography>
                  <Chip
                    size="small"
                    label={a.sev.toUpperCase()}
                    sx={{ borderRadius: 999 }}
                    color={
                      a.sev === "success"
                        ? "success"
                        : a.sev === "warning"
                        ? "warning"
                        : "info"
                    }
                  />
                </Stack>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {a.d}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </MetallicTile>
      </Box>
    </Box>
  );
}
